import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Button, type DialogProps, TextField } from "@mui/material";
import {
	CartTemplateModalBody,
	CartTemplateModalButtons,
	CartTemplateModalHeader,
	SaveButton,
	SavingIcon
} from "features/MarketingMaterials/pages/Checkout/components/ViewCartModals/CartTemplateModal.styles.ts";
import Close from "@mui/icons-material/Close";
import { CharacterLimitExceeded, RequiredFieldNotice } from "constants/text.ts";
import HelperText from "components/Form/HelperText/HelperText.tsx";

export interface SaveCartTemplateModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (cartName: string) => Promise<void>;
	isSaving: boolean;
}

export const CART_NAME_MAX_CHARS = 50;

export const SaveCartTemplateModal = ({ isOpen, onClose, onSubmit, isSaving }: SaveCartTemplateModalProps) => {
	const [cartName, setCartName] = useState<string>("");
	const [cartNameHasError, setCartNameHasError] = useState(false);
	const [errorText, setErrorText] = useState<string | null>("");

	const handleClose = () => {
		setCartName("");
		onClose();
	};

	const handleDialogClose: DialogProps["onClose"] = (_event, reason) => {
		if (reason === "backdropClick") return;
		handleClose();
	};

	const handleSubmit = async () => {
		if (!cartName) {
			return;
		}

		try {
			await onSubmit(cartName);
			handleClose();
		} catch (error: any) {
			const errorMessage = error?.data?.errors.map((e: any) => e.message).join(", ");
			setCartNameHasError(true);
			setErrorText(errorMessage);
		}
	};

	const checkForErrors = () => {
		if (cartName.length > CART_NAME_MAX_CHARS) return;

		if (cartName.trim() === "") {
			setCartNameHasError(true);
			setErrorText("Please enter a cart name to proceed.");
		} else {
			handleSubmit().catch((e) => console.error(e));
		}
	};

	function cartNameHelperText(): import("react").ReactNode {
		if (cartNameHasError) return errorText;
		if (cartName.length > CART_NAME_MAX_CHARS) return CharacterLimitExceeded;
		return "";
	}

	return (
		<Dialog
			open={isOpen}
			onClose={handleDialogClose}
			fullWidth
			maxWidth="sm"
		>
			<CartTemplateModalHeader>
				<div>
					<h6>Save This Cart</h6>
					<p>
						Saving will allow you to refill your cart with the current items at any time. Saved carts can be
						loaded over and over. Saving a cart does not guarantee or hold inventory.
					</p>
				</div>
				<Close onClick={handleClose} />
			</CartTemplateModalHeader>
			<CartTemplateModalBody isError={cartNameHasError || cartName.length > CART_NAME_MAX_CHARS}>
				<p>{RequiredFieldNotice}</p>
				<TextField
					label="Cart Name"
					value={cartName}
					data-testid="save-draft-input"
					onChange={(e) => {
						setCartNameHasError(false);
						setCartName(e.target.value);
					}}
					sx={{ marginBottom: "4px" }}
				/>
				<HelperText
					isError={cartNameHasError || cartName.length > CART_NAME_MAX_CHARS}
					text={cartNameHelperText()}
					characterLimit={{
						currentLength: cartName.length,
						limit: CART_NAME_MAX_CHARS,
						isLimitError: cartName.length > CART_NAME_MAX_CHARS
					}}
				/>
				<CartTemplateModalButtons>
					<Button
						variant="outlined"
						onClick={handleClose}
					>
						Cancel
					</Button>
					<SaveButton
						variant="contained"
						onClick={handleSubmit}
						disabled={!cartName}
						endIcon={isSaving ? <SavingIcon size={20} /> : <></>}
					>
						Save Cart
					</SaveButton>
				</CartTemplateModalButtons>
			</CartTemplateModalBody>
		</Dialog>
	);
};
